import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  loginOtp,
  verifyOtpV2,
  logoutApi,
  getProfile,
  updateProfile,
  resendOtp,
  loginPassword,
  signup,
  forgotPassword,
  verifyForgotOtp,
  resetPassword,
} from "../services/api";
import {
  clearAuth,
  getStoredToken,
  getStoredUser,
  saveAuth,
} from "../utils/authStorage";

export const sendOtpLogin = createAsyncThunk(
  "auth/sendOtpLogin",
  async (payload, { rejectWithValue }) => {
    try {
      return await loginOtp(payload);
    } catch (err) {
      return rejectWithValue(err.message || "Failed to send OTP");
    }
  }
);

export const verifyOtpLogin = createAsyncThunk(
  "auth/verifyOtpLogin",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await verifyOtpV2(payload);
      if (res?.token) saveAuth(res.token, res.data);
      return res;
    } catch (err) {
      return rejectWithValue(err.message || "OTP verification failed");
    }
  }
);

export const resendOtpAction = createAsyncThunk(
  "auth/resendOtp",
  async (payload, { rejectWithValue }) => {
    try {
      return await resendOtp(payload);
    } catch (err) {
      return rejectWithValue(err.message || "Failed to resend OTP");
    }
  }
);

export const passwordLogin = createAsyncThunk(
  "auth/passwordLogin",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await loginPassword(payload);
      if (res?.token) saveAuth(res.token, res.data);
      return res;
    } catch (err) {
      return rejectWithValue(err.message || "Login failed");
    }
  }
);

export const signupUser = createAsyncThunk(
  "auth/signup",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await signup(payload);
      if (res?.token) saveAuth(res.token, res.data);
      return res;
    } catch (err) {
      return rejectWithValue(err.message || "Signup failed");
    }
  }
);

export const fetchProfile = createAsyncThunk(
  "auth/fetchProfile",
  async (_, { rejectWithValue }) => {
    try {
      const res = await getProfile();
      if (res?.data) saveAuth(getStoredToken(), res.data);
      return res;
    } catch (err) {
      return rejectWithValue(err.message || "Failed to load profile");
    }
  }
);

export const saveProfile = createAsyncThunk(
  "auth/saveProfile",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await updateProfile(payload);
      if (res?.data) saveAuth(getStoredToken(), res.data);
      return res;
    } catch (err) {
      return rejectWithValue(err.message || "Failed to update profile");
    }
  }
);

export const logoutUser = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    try {
      await logoutApi();
    } catch (err) {
      // still clear local session
    } finally {
      clearAuth();
    }
    return true;
  }
);

export const requestForgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async (payload, { rejectWithValue }) => {
    try {
      return await forgotPassword(payload);
    } catch (err) {
      return rejectWithValue(err.message || "Failed to send reset code");
    }
  }
);

export const verifyResetOtp = createAsyncThunk(
  "auth/verifyResetOtp",
  async (payload, { rejectWithValue }) => {
    try {
      return await verifyForgotOtp(payload);
    } catch (err) {
      return rejectWithValue(err.message || "Invalid OTP");
    }
  }
);

export const setNewPassword = createAsyncThunk(
  "auth/resetPassword",
  async (payload, { rejectWithValue }) => {
    try {
      return await resetPassword(payload);
    } catch (err) {
      return rejectWithValue(err.message || "Failed to reset password");
    }
  }
);

const initialState = {
  token: getStoredToken(),
  user: getStoredUser(),
  isLoading: false,
  error: null,
  otpSent: false,
  mobileNumber: "",
  message: "",
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearAuthError: (state) => {
      state.error = null;
      state.message = "";
    },
    resetOtpFlow: (state) => {
      state.otpSent = false;
      state.mobileNumber = "";
      state.error = null;
      state.message = "";
    },
    hydrateAuth: (state) => {
      state.token = getStoredToken();
      state.user = getStoredUser();
    },
  },
  extraReducers: (builder) => {
    const pending = (state) => {
      state.isLoading = true;
      state.error = null;
      state.message = "";
    };
    const rejected = (state, action) => {
      state.isLoading = false;
      state.error = action.payload || "Request failed";
    };

    builder
      .addCase(sendOtpLogin.pending, pending)
      .addCase(sendOtpLogin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.otpSent = true;
        state.mobileNumber = action.meta.arg?.mobileNumber || "";
        state.message = action.payload?.message || "OTP sent";
        if (action.payload?.data) state.user = action.payload.data;
      })
      .addCase(sendOtpLogin.rejected, rejected)

      .addCase(verifyOtpLogin.pending, pending)
      .addCase(verifyOtpLogin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.token = action.payload?.token || "";
        state.user = action.payload?.data || null;
        state.otpSent = false;
        state.message = "Logged in successfully";
      })
      .addCase(verifyOtpLogin.rejected, rejected)

      .addCase(resendOtpAction.pending, pending)
      .addCase(resendOtpAction.fulfilled, (state, action) => {
        state.isLoading = false;
        state.message = action.payload?.message || "OTP resent";
      })
      .addCase(resendOtpAction.rejected, rejected)

      .addCase(passwordLogin.pending, pending)
      .addCase(passwordLogin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.token = action.payload?.token || "";
        state.user = action.payload?.data || null;
        state.message = "Logged in successfully";
      })
      .addCase(passwordLogin.rejected, rejected)

      .addCase(signupUser.pending, pending)
      .addCase(signupUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.token = action.payload?.token || "";
        state.user = action.payload?.data || null;
        state.message = "Account created";
      })
      .addCase(signupUser.rejected, rejected)

      .addCase(fetchProfile.pending, pending)
      .addCase(fetchProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload?.data || null;
      })
      .addCase(fetchProfile.rejected, rejected)

      .addCase(saveProfile.pending, pending)
      .addCase(saveProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload?.data || state.user;
        state.message = "Profile updated";
      })
      .addCase(saveProfile.rejected, rejected)

      .addCase(logoutUser.fulfilled, (state) => {
        state.isLoading = false;
        state.token = "";
        state.user = null;
        state.otpSent = false;
        state.message = "Logged out";
      })

      .addCase(requestForgotPassword.pending, pending)
      .addCase(requestForgotPassword.fulfilled, (state, action) => {
        state.isLoading = false;
        state.message = action.payload?.message || "Reset code sent";
      })
      .addCase(requestForgotPassword.rejected, rejected)

      .addCase(verifyResetOtp.pending, pending)
      .addCase(verifyResetOtp.fulfilled, (state, action) => {
        state.isLoading = false;
        state.message = action.payload?.message || "OTP verified";
      })
      .addCase(verifyResetOtp.rejected, rejected)

      .addCase(setNewPassword.pending, pending)
      .addCase(setNewPassword.fulfilled, (state, action) => {
        state.isLoading = false;
        state.message = action.payload?.message || "Password updated";
      })
      .addCase(setNewPassword.rejected, rejected);
  },
});

export const { clearAuthError, resetOtpFlow, hydrateAuth } = authSlice.actions;
export const selectIsAuthenticated = (state) => Boolean(state.auth.token);
export default authSlice.reducer;
